// src/canvas/placeholders/Placeholder.js
import { fabric } from "fabric";

export const PLACEHOLDER_TYPES = {
  TEXT: "text",
  IMAGE: "image",
};

const dashed = [4, 4];
function clamp(v, a, b) {
  return Math.max(a, Math.min(b, v));
}

/**
 * Auto-fit font size so textbox fits into available height (only shrinks).
 * Ensure it never goes below minFS.
 */
function autofitTextbox(tb, availableH, minFS = 12, maxFS = 72) {
  let fs = clamp(tb.fontSize ?? 18, minFS, maxFS);
  tb.set({ fontSize: fs });
  tb.initDimensions();

  let guard = 0;
  while (tb.height > availableH && fs > minFS && guard++ < 80) {
    fs -= 1;
    tb.set({ fontSize: fs });
    tb.initDimensions();
  }

  // final safety: enforce minimum
  if (tb.fontSize < minFS) {
    tb.set({ fontSize: minFS });
    tb.initDimensions();
  }
}

/**
 * Creates a "placeholder" group that behaves like a PPT placeholder:
 * - dashed rect + Textbox inside
 * - resizing the group resizes the rect and textbox (no visual scaling)
 * - clicking the placeholder switches it into editing mode (textbox editable)
 * - when editing finishes and text is empty -> restore placeholder text
 */
export function createPlaceholder(canvas, opts = {}) {
  const {
    left = 100,
    top = 100,
    width = 400,
    height = 120,
    type = PLACEHOLDER_TYPES.TEXT,
    hint = "Click to add text",
    bullet = false,
    padding = 16,
    minWidth = 160,
    minHeight = 70,
    stroke = "#9aa0a6",
    strokeWidth = 1,
    strokeDashArray = dashed,
    title = false,
    initialText: customInitialText,
    fontSize: customFontSize,
  } = opts;

  const rect = new fabric.Rect({
    left: 0,
    top: 0,
    width,
    height,
    fill: null,
    stroke,
    strokeDashArray,
    strokeWidth,
    rx: 0,
    ry: 0,
    selectable: false,
    evented: false,
  });

  // Decide initial text
  const defaultInitial = title ? "Click to add title" : hint;
  const initialText = customInitialText ?? (bullet ? "• " + defaultInitial : defaultInitial);

  // Decide font size
  const defaultFontSize = title ? 36 : 18;
  const fontSize = customFontSize ?? defaultFontSize;

  const tb = new fabric.Textbox(initialText, {
    left: padding,
    top: padding,
    width: width - padding * 2,
    fontSize,
    fontFamily: "Arial",
    fill: "#000", // default text fill (we'll keep placeholder style via dashed rect)
    editable: false, // not editable until clicked
    objectCaching: false,
  });

  tb.on("changed", () => {
  console.log("TEXTBOX CHANGED:", {
    text: tb.text,
    fill: tb.fill,
    editable: tb.editable,
    hiddenTextarea: tb.hiddenTextarea?.style,
  });
  tb.initDimensions();
  tb.setCoords();

  // mark parent group dirty so it re-renders
  if (tb.group) tb.group.dirty = true;

  canvas.requestRenderAll();
});


  // Fit once on create (never shrink below these min font sizes)
  autofitTextbox(tb, height - padding * 2, title ? 18 : 12, title ? 48 : 28);

  const group = new fabric.Group([rect, tb], {
    left,
    top,
    scaleX: 1,
    scaleY: 1,
    hasRotatingPoint: false,
    lockScalingFlip: true,
  });

  // metadata
  group.phMeta = { type, padding, minWidth, minHeight, title, minFont: title ? 18 : 12 };
  group.getRect = () => rect;
  group.getTextbox = () => tb;

  // Scaling -> convert scale into width/height (no visual scaling)
  group.on("scaling", () => {
    const w = clamp(rect.width * group.scaleX, minWidth, 99999);
    const h = clamp(rect.height * group.scaleY, minHeight, 99999);

    // reset visual scale
    group.set({ scaleX: 1, scaleY: 1 });

    rect.set({ width: w, height: h });
    tb.set({ width: w - padding * 2, left: padding, top: padding });

    autofitTextbox(tb, h - padding * 2, group.phMeta.minFont, title ? 48 : 28);

    group.set({ width: w, height: h });
    group.dirty = true;
    canvas.requestRenderAll();
  });

  group.on("modified", () => {
    // normalize scale after modifications
    group.set({ scaleX: 1, scaleY: 1 });
  });

  // --- Edit mode behavior (clicking placeholder enters text edit) ---
  group.on("mousedown", (opt) => {
    // stop propagation so outer canvas handlers (like panning) don't interfere
    try { opt.e && opt.e.stopPropagation && opt.e.stopPropagation(); } catch (err) {}

    if (!tb.editable) {
      // Make sure font size is not ridiculously small before editing
      const minFS = group.phMeta.minFont || (title ? 18 : 12);
      if (!tb.fontSize || tb.fontSize < minFS) {
        tb.set({ fontSize: minFS });
        tb.initDimensions();
      }

      // Switch visuals: editable textbox (fill black), hide dashed rect while editing
      tb.set({ editable: true, fill: "#000" });
      if (tb.text === initialText) tb.text = "";
      rect.set({ stroke: null, strokeDashArray: [] });

      // ensure no other active object interferes
      canvas.discardActiveObject();
      canvas.requestRenderAll();

      // set the textbox as active and enter editing
      canvas.setActiveObject(tb);
      tb.enterEditing();

      // IMPORTANT: fix hidden textarea styling (the browser element Fabric creates)
      // This ensures font metrics & caret match the textbox font on canvas.
      setTimeout(() => {
        if (tb.hiddenTextarea) {
          const fsPx = Math.max(tb.fontSize || minFS, minFS) + "px";
          try {
            tb.hiddenTextarea.style.fontSize = fsPx;
            tb.hiddenTextarea.style.color = tb.fill || "#000";
            tb.hiddenTextarea.style.caretColor = tb.fill || "#000";

            // make the input area reasonably sized (keeps composition sensible)
            tb.hiddenTextarea.style.width = Math.max(40, Math.round(tb.width)) + "px";
            tb.hiddenTextarea.style.height = Math.max(20, Math.round(tb.height)) + "px";

            // keep textarea offscreen but ensure metrics are correct (opacity 0 is fine)
            tb.hiddenTextarea.style.opacity = "0";
            tb.hiddenTextarea.style.zIndex = "-999";
          } catch (err) {
            // some browsers may lock style updates — still safe to ignore
            // console.warn("Failed to style hidden textarea:", err);
          }
          // focus the textarea so user can type
          try { tb.hiddenTextarea.focus(); } catch (err) {}
        }
        canvas.requestRenderAll();
      }, 0);
    }
  });

  // When editing starts, again ensure hidden textarea styling is updated
  tb.on("editing:entered", () => {
    const interval = setInterval(() => {
    if (tb.isEditing) {
      if (tb.group) tb.group.dirty = true;
      canvas.requestRenderAll();
    } else {
      clearInterval(interval);
    }
  }, 100);
    if (tb.hiddenTextarea) {
      try {
        tb.hiddenTextarea.style.color = tb.fill || "#000";
        tb.hiddenTextarea.style.caretColor = tb.fill || "#000";
        tb.hiddenTextarea.style.fontSize = Math.max(tb.fontSize || group.phMeta.minFont, group.phMeta.minFont) + "px";
      } catch (err) {}
    }
  });

  // When editing finishes
  tb.on("editing:exited", () => {
    tb.set({ editable: false });
    // If user left empty -> restore placeholder hint
    if (!tb.text || !tb.text.trim()) {
      tb.text = initialText;
      rect.set({ stroke, strokeDashArray });
    } else {
      rect.set({ stroke: null });
    }

    // make sure font size still reasonable after editing
    autofitTextbox(tb, (group.height || height) - padding * 2, group.phMeta.minFont, title ? 48 : 28);

    // remove hidden textarea styling hack (optional)
    if (tb.hiddenTextarea) {
      try {
        tb.hiddenTextarea.style.opacity = "0";
      } catch (e) {}
    }

    canvas.discardActiveObject();
    canvas.requestRenderAll();
  });

  // Don't allow selecting the child box pieces separately
  group.forEachObject((o) => (o.selectable = false));
  group.setControlsVisibility({ mtr: false });

  return group;
}
